import { useEffect, useState, type ComponentType, type ReactNode } from 'react'
import { useAuth } from '../../context/useAuth'
import { firebaseAuth } from '../../services/firebase'
import { getWrapperRole } from '../../utils/wrapperRole'
import { AccessDenied } from '../Common/AccessDenied'
import { FullscreenLoader } from '../Common/FullscreenLoader'
import { Login } from './Login'
import { getNlsString } from '../../nls/nlsRuntime'

export type ClaimRole = 'ADMIN' | 'EDITOR' | 'VIEWER' | 'READONLY' | 'HELPER'

type RoleProtectedRouteProps = {
  allowedRoles: ClaimRole[]
  children: ReactNode
  redirectTo?: string
}

const claimKeyForRole = (role: ClaimRole): string => role.toLowerCase()

const roleAllowedByClaims = async (allowedRoles: ClaimRole[]): Promise<boolean> => {
  const user = firebaseAuth.currentUser
  if (!user) return false
  const token = await user.getIdTokenResult(true)
  return allowedRoles.some((role) => token.claims[claimKeyForRole(role)] === true || token.claims.role === role)
}

export const RoleProtectedRoute = ({ allowedRoles, children, redirectTo = '/' }: RoleProtectedRouteProps) => {
  const { currentUser, isAuthLoading } = useAuth()
  const [allowed, setAllowed] = useState<boolean | null>(null)

  useEffect(() => {
    let cancelled = false
    if (isAuthLoading) {
      setAllowed(null)
      return
    }
    if (!currentUser) {
      setAllowed(false)
      return
    }
    void roleAllowedByClaims(allowedRoles).then((result) => {
      if (!cancelled) setAllowed(result)
    }).catch(() => {
      if (!cancelled) setAllowed(false)
    })
    return () => {
      cancelled = true
    }
  }, [allowedRoles, currentUser, isAuthLoading])

  useEffect(() => {
    if (allowed !== false || !currentUser || typeof window === 'undefined') return
    if (getWrapperRole() !== null) return
    window.history.replaceState(null, '', redirectTo)
    window.dispatchEvent(new PopStateEvent('popstate'))
  }, [allowed, currentUser, redirectTo])

  if (isAuthLoading || allowed === null) return (
    <FullscreenLoader waitForReady={isAuthLoading || allowed === null} onFinish={() => {}} />
  )
  if (!currentUser) return <Login />
  if (!allowed) {
    if (getWrapperRole() !== null) {
      return <AccessDenied message={getNlsString('NLSHelperAppHelperOnly')} />
    }
    return <Login />
  }
  return <>{children}</>
}

export const withRoleProtectedRoute = <P extends object>(
  Component: ComponentType<P>,
  allowedRoles: ClaimRole[],
  redirectTo?: string,
) => {
  const Guarded = (props: P) => (
    <RoleProtectedRoute allowedRoles={allowedRoles} redirectTo={redirectTo}>
      <Component {...props} />
    </RoleProtectedRoute>
  )
  Guarded.displayName = `RoleProtectedRoute(${Component.displayName || Component.name || 'Component'})`
  return Guarded
}
